/**
 * JEDNO źródło prawdy progów palet współdzielone między aplikacją (TS przez
 * src/lib/legend.ts) a silnikami renderowania Node (src/rendering/, CLI).
 * M006: kończy duplikację progów w skryptach (wcześniej kopiowane w
 * scripts/render-opera-sample.mjs z komentarzem provenance).
 *
 * DWD: DOKŁADNE wartości serwera — pobrane live z GetStyles (2026-07-19),
 * warstwa wn-analysis, styl "reflectivity"; każdy wpis to DOLNA granica klasy
 * dBZ wg etykiet SLD. Pominięte: no-data i w pełni przezroczysta klasa <7 dBZ.
 * Nic nie jest wymyślone ani interpolowane. Provenance: rekord
 * eu-de-dwd-wn-analysis (paletteSource) + src/lib/legend.ts.
 */
import { FIELD_ENCODING_RATE, quantizeFieldValue } from "./fieldEncodings.mjs";

/** @type {Array<{ dbz: number, color: string }>} */
export const DWD_REFLECTIVITY_STOPS_RAW = [
  { dbz: 7, color: "#99ffff" },
  { dbz: 9.5, color: "#33ffff" },
  { dbz: 12, color: "#00caca" },
  { dbz: 14.5, color: "#009934" },
  { dbz: 19, color: "#4dbf1a" },
  { dbz: 23.5, color: "#99cc00" },
  { dbz: 28, color: "#cce600" },
  { dbz: 32.5, color: "#ffff00" },
  { dbz: 37, color: "#ffc400" },
  { dbz: 41.5, color: "#ff8900" },
  { dbz: 46, color: "#ff0000" },
  { dbz: 50.5, color: "#b40000" },
  { dbz: 55, color: "#4848ff" },
  { dbz: 60, color: "#0000ca" },
  { dbz: 65, color: "#990099" },
  { dbz: 75, color: "#ff33ff" },
  { dbz: 85, color: "#000000" }
];

/**
 * Wspólna laboratoryjna rampa RATE (mm/h) — DOKŁADNE kolory i granice klas z
 * oficjalnego stylu opadowego DWD: GetStyles (2026-07-22), warstwa produktu RV
 * na publicznym WMS DWD (GeoNutzV). Etykiety SLD są w mm/h (quantity SLD to
 * mm/5min = górna granica klasy); tu wpisy = DOLNA granica klasy w mm/h.
 * Pominięte: no-data i przezroczysta klasa <0,1 mm/h. Nic nie jest wymyślone.
 * Fixture-dowód: test/fixtures/rendering/dwd-rv-getstyles.sld.
 * Uwaga licencyjna: używamy wyłącznie STYLU (rampy) z publicznego WMS — dane
 * produktu RV pozostają poza zakresem Lab (nota w rekordzie RADOLAN).
 */

/** @type {Array<{ rate: number, color: string }>} */
export const DWD_RV_RATE_STOPS_RAW = [
  { rate: 0.1, color: "#33ffff" },
  { rate: 0.2, color: "#1acc9a" },
  { rate: 0.4, color: "#019934" },
  { rate: 1.0, color: "#4db31b" },
  { rate: 2.0, color: "#99cc01" },
  { rate: 3.0, color: "#cce601" },
  { rate: 5.0, color: "#ffff01" },
  { rate: 7.5, color: "#ffc401" },
  { rate: 10, color: "#ff8901" },
  { rate: 15, color: "#ff4501" },
  { rate: 30, color: "#fe0000" },
  { rate: 45, color: "#e5004c" },
  { rate: 75, color: "#cc0098" },
  { rate: 100, color: "#6600cb" },
  { rate: 150, color: "#0000fe" }
];

/**
 * Kolor dla intensywności opadu (mm/h) wg rampy RV: null poniżej 0,1 mm/h.
 * @param {number} rate
 * @returns {string|null}
 */
export function dwdRvColorForRate(rate) {
  if (Number.isNaN(rate) || rate < DWD_RV_RATE_STOPS_RAW[0].rate) return null;
  let color = null;
  for (const stop of DWD_RV_RATE_STOPS_RAW) {
    if (rate >= stop.rate) color = stop.color;
    else break;
  }
  return color;
}

/**
 * Kolor dla wartości dBZ wg rampy DWD: null poniżej pierwszego progu
 * (przezroczyste), inaczej ostatni próg ≤ wartości.
 * @param {number} dbz
 * @returns {string|null}
 */
export function dwdColorForDbz(dbz) {
  if (Number.isNaN(dbz) || dbz < DWD_REFLECTIVITY_STOPS_RAW[0].dbz) return null;
  let color = null;
  for (const stop of DWD_REFLECTIVITY_STOPS_RAW) {
    if (dbz >= stop.dbz) color = stop.color;
    else break;
  }
  return color;
}

/**
 * M009 (D-008) — wspólna paleta laboratoryjna „us-reflectivity-classic":
 * klasyczna amerykańska skala odbiciowości w konwencji NWS (RIDGE/NIDS),
 * 15 klas 5–75 dBZ co 5 dBZ (turkus→błękity→zielenie→żółcie→czerwienie→
 * fiolet→biel). STATUS ŹRÓDŁA (uczciwie): to jest PALETA LABORATORYJNA
 * wzorowana na standardzie NWS (dozwolona wprost przez bramkę D walidacji —
 * „jasno udokumentowana wspólna paleta laboratoryjna"); hex odpowiadają
 * powszechnie publikowanej tablicy legendy NWS RIDGE, ale NIE zostały pobrane
 * maszynowo z oficjalnego stylu (NWS nie publikuje SLD/GetStyles jak DWD).
 * Follow-up w backlogu: podmiana na wartości z oficjalnego źródła maszynowego,
 * jeśli takie znajdziemy. NIE jest to kopia własnościowej palety The Weather
 * Channel — wyłącznie standardowa konwencja NWS.
 */

/** @type {Array<{ dbz: number, color: string }>} */
export const US_REFLECTIVITY_CLASSIC_STOPS_RAW = [
  { dbz: 5, color: "#04e9e7" },
  { dbz: 10, color: "#019ff4" },
  { dbz: 15, color: "#0300f4" },
  { dbz: 20, color: "#02fd02" },
  { dbz: 25, color: "#01c501" },
  { dbz: 30, color: "#008e00" },
  { dbz: 35, color: "#fdf802" },
  { dbz: 40, color: "#e5bc00" },
  { dbz: 45, color: "#fd9500" },
  { dbz: 50, color: "#fd0000" },
  { dbz: 55, color: "#d40000" },
  { dbz: 60, color: "#bc0000" },
  { dbz: 65, color: "#f800fd" },
  { dbz: 70, color: "#9854c6" },
  { dbz: 75, color: "#fdfdfd" }
];

/**
 * Kolor dla wartości dBZ wg klasycznej skali US (NWS): null poniżej 5 dBZ
 * (przezroczyste), inaczej ostatni próg ≤ wartości.
 * @param {number} dbz
 * @returns {string|null}
 */
export function usReflectivityClassicColorForDbz(dbz) {
  if (Number.isNaN(dbz) || dbz < US_REFLECTIVITY_CLASSIC_STOPS_RAW[0].dbz) return null;
  let color = null;
  for (const stop of US_REFLECTIVITY_CLASSIC_STOPS_RAW) {
    if (dbz >= stop.dbz) color = stop.color;
    else break;
  }
  return color;
}

/**
 * M011-B — paleta LABORATORYJNA MESH (grad, mm średnicy, ESTYMATA radarowa).
 * STATUS ŹRÓDŁA (uczciwie): NOAA nie publikuje maszynowego stylu dla MESH —
 * progi osadzone w POWSZECHNIE UŻYWANYCH kategoriach rozmiaru gradu NWS
 * (severe od 1" = 25 mm; golf 44 mm; tenis 64 mm; baseball 70 mm; softball
 * ~100 mm), kolory własne Lab (zimne→gorące→fiolet dla ekstremów; celowo
 * INNA rodzina barw niż rampa dBZ, żeby warstwy nie były mylone). Podlega
 * bramce akceptacji WIZUALNEJ właściciela przed publikacją (konstytucja).
 */

/** @type {Array<{ mm: number, color: string }>} */
export const US_MESH_LAB_STOPS_RAW = [
  { mm: 5, color: "#b3ecd1" },
  { mm: 10, color: "#6fdc9e" },
  { mm: 15, color: "#f5ee7a" },
  { mm: 20, color: "#ffcf5c" },
  { mm: 25, color: "#ff9f1c" },
  { mm: 32, color: "#ff6b35" },
  { mm: 38, color: "#f03e3e" },
  { mm: 44, color: "#c92a2a" },
  { mm: 50, color: "#a61e4d" },
  { mm: 64, color: "#862e9c" },
  { mm: 70, color: "#5f3dc4" },
  { mm: 100, color: "#2b1b6b" }
];

/**
 * Kolor dla MESH w mm: null poniżej 5 mm (przezroczyste — brak istotnego
 * gradu), inaczej ostatni próg ≤ wartości.
 * @param {number} mm
 * @returns {string|null}
 */
export function usMeshLabColorForMm(mm) {
  if (Number.isNaN(mm) || mm < US_MESH_LAB_STOPS_RAW[0].mm) return null;
  let color = null;
  for (const stop of US_MESH_LAB_STOPS_RAW) {
    if (mm >= stop.mm) color = stop.color;
    else break;
  }
  return color;
}

/**
 * M011-C — paleta LABORATORYJNA NLDN (gęstość wyładowań doziemnych CG,
 * błysk/km²/min, produkt sieci NLDN w MRMS). STATUS ŹRÓDŁA (uczciwie):
 * NOAA nie publikuje maszynowego stylu; progi dobrane EMPIRYCZNIE z klatki
 * burzowej 2026-08-07 19:00Z (168 px, zakres 0,5–2,9; kwantyzacja 0,001)
 * tak, by realne burze rozpinały rampę; kolory = klasyczny język
 * "wyładowaniowy" (żółć→pomarańcz→czerwień→fiolet dla ekstremów). Podlega
 * bramce akceptacji WIZUALNEJ właściciela przed publikacją (konstytucja).
 */

/** @type {Array<{ density: number, color: string }>} */
export const US_NLDN_LAB_STOPS_RAW = [
  { density: 0.2, color: "#ffec99" },
  { density: 0.5, color: "#ffd43b" },
  { density: 1, color: "#fab005" },
  { density: 1.5, color: "#f76707" },
  { density: 2, color: "#e8590c" },
  { density: 3, color: "#d9480f" },
  { density: 5, color: "#c92a2a" },
  { density: 8, color: "#862e9c" }
];

/**
 * Kolor dla gęstości CG (błysk/km²/min): null poniżej 0,2 (przezroczyste),
 * inaczej ostatni próg ≤ wartości.
 * @param {number} density
 * @returns {string|null}
 */
export function usNldnLabColorForDensity(density) {
  if (Number.isNaN(density) || density < US_NLDN_LAB_STOPS_RAW[0].density) return null;
  let color = null;
  for (const stop of US_NLDN_LAB_STOPS_RAW) {
    if (density >= stop.density) color = stop.color;
    else break;
  }
  return color;
}

/**
 * M011-C — paleta LABORATORYJNA prawdopodobieństwa wyładowań (%, oficjalna
 * PROGNOZA NOAA/MRMS LightningProbabilityNext30/60minGrid; wartości całkowite
 * 1–95 zmierzone 2026-08-08). Rampa FIOLETOWA celowo inna niż wszystkie
 * warstwy obserwacyjne — fiolet = prognoza, nie pomiar. Podlega bramce
 * akceptacji WIZUALNEJ właściciela przed publikacją (konstytucja).
 */

/** @type {Array<{ pct: number, color: string }>} */
export const US_LIGHTNING_PROB_LAB_STOPS_RAW = [
  { pct: 5, color: "#e3d9ff" },
  { pct: 15, color: "#c5b3f6" },
  { pct: 30, color: "#a58af0" },
  { pct: 50, color: "#845ef7" },
  { pct: 70, color: "#6a3ee8" },
  { pct: 90, color: "#4c2ab3" }
];

/**
 * Kolor dla prawdopodobieństwa (%): null poniżej 5% (przezroczyste),
 * inaczej ostatni próg ≤ wartości.
 * @param {number} pct
 * @returns {string|null}
 */
export function usLightningProbLabColorForPct(pct) {
  if (Number.isNaN(pct) || pct < US_LIGHTNING_PROB_LAB_STOPS_RAW[0].pct) return null;
  let color = null;
  for (const stop of US_LIGHTNING_PROB_LAB_STOPS_RAW) {
    if (pct >= stop.pct) color = stop.color;
    else break;
  }
  return color;
}

/**
 * M012+ — paleta KONSUMENCKA odbiciowości (dark-first; projekt
 * docs/DESIGN-PREMIUM-RADAR.md §3, ZAAKCEPTOWANA przez właściciela
 * 2026-08-08 wieczorem na próbce z surowego pola — „no spoko dawaj tylko
 * jako opcje"). 5 klas w paśmie 20–40 dBZ (widoczność małych ech); alfa
 * wbudowana w stopy (słaby opad półprzezroczysty — głębia); klasyczna NWS
 * pozostaje jako opcja „pro".
 */

/** @type {Array<{ dbz: number, color: string, alpha: number }>} */
export const US_REFLECTIVITY_CONSUMER_STOPS_RAW = [
  { dbz: 5, color: "#274a6d", alpha: 0.5 },
  { dbz: 10, color: "#2b6394", alpha: 0.62 },
  { dbz: 15, color: "#2f83b8", alpha: 0.72 },
  { dbz: 20, color: "#2fa6a4", alpha: 0.82 },
  { dbz: 25, color: "#3dbd63", alpha: 0.88 },
  { dbz: 30, color: "#8ed03e", alpha: 0.94 },
  { dbz: 35, color: "#f2e24b", alpha: 1 },
  { dbz: 40, color: "#f7a72d", alpha: 1 },
  { dbz: 45, color: "#f2632a", alpha: 1 },
  { dbz: 50, color: "#e6302e", alpha: 1 },
  { dbz: 55, color: "#c2185b", alpha: 1 },
  { dbz: 60, color: "#a516b4", alpha: 1 },
  { dbz: 65, color: "#cf52e6", alpha: 1 },
  { dbz: 70, color: "#eda6f5", alpha: 1 },
  { dbz: 75, color: "#ffffff", alpha: 1 }
];

const a2hex = (a) => Math.round(a * 255).toString(16).padStart(2, "0");

/**
 * Wariant WYGŁADZONY: ciągła LUT — interpolacja liniowa koloru i alfy
 * MIĘDZY stopami NA POLU dBZ (standard branżowy: nigdy na gotowych RGBA;
 * docs/research/SMOOTH-RADAR-TECHNIQUES.md). Zwraca #rrggbbaa.
 * @param {number} dbz
 * @returns {string|null}
 */
export function usReflectivityConsumerSmoothColorForDbz(dbz) {
  const S = US_REFLECTIVITY_CONSUMER_STOPS_RAW;
  if (Number.isNaN(dbz)) return null;
  // Kwantyzacja LUT do 0,5 dBZ — PRZED progiem widoczności (M013 F3b, W6):
  // tekstura pola niesie dBZ już skwantyzowane do siatki 0,5 (kontrakt
  // radar-lab/us-field/1), więc próg liczony na surowym dBZ dawał rozjazd
  // serwer↔shader na pikselach granicznych (zmierzone: 12/2811 próbek E2E).
  // Jedna semantyka = równość DOKŁADNA ścieżek; przesunięcie progu ≤0,25 dBZ
  // na krawędzi przezroczystości. (Defensywnie też dla drobniejszych kroków.)
  dbz = Math.round(dbz * 2) / 2;
  if (dbz < S[0].dbz) return null;
  const last = S[S.length - 1];
  if (dbz >= last.dbz) return `${last.color}${a2hex(last.alpha)}`;
  for (let i = 0; i < S.length - 1; i++) {
    const a = S[i], b = S[i + 1];
    if (dbz >= a.dbz && dbz < b.dbz) {
      const t = (dbz - a.dbz) / (b.dbz - a.dbz);
      const ch = (h1, h2, off) => Math.round(
        parseInt(h1.slice(off, off + 2), 16) * (1 - t) + parseInt(h2.slice(off, off + 2), 16) * t
      ).toString(16).padStart(2, "0");
      return `#${ch(a.color, b.color, 1)}${ch(a.color, b.color, 3)}${ch(a.color, b.color, 5)}${a2hex(a.alpha * (1 - t) + b.alpha * t)}`;
    }
  }
  return null;
}

/**
 * M013 F5 „opad na shader" (2026-08-11) — wariant FIELD rampy RV: te same
 * stopy i schodki co dwdRvColorForRate, ale wartość jest NAJPIERW kwantyzowana
 * do siatki kodowania tekstury pola mm/h (log10 zakotwiczone w 0,1 mm/h —
 * src/lib/fieldEncodings.mjs). Jedna semantyka serwer↔shader (W6): RGBA
 * renderu == LUT shadera z tekstury CO DO BAJTA. Rejestrowana w render.mjs
 * jako "dwd-rv-rate-15-field"; bazowa dwdRvColorForRate pozostaje NIETKNIĘTA
 * (profile RADOLAN/Explorer bez zmian bajtowych).
 * @param {number} rate
 * @returns {string|null}
 */
export function dwdRvRateFieldColorForRate(rate) {
  return dwdRvColorForRate(quantizeFieldValue(rate, FIELD_ENCODING_RATE));
}
